/**
 * LiveNowPill — the "we're broadcasting RIGHT NOW" indicator that lives
 * in the global top navbar.
 *
 * Conversion goals:
 *   • Pull cold visitors into /live without forcing them to know we
 *     even have a daily show. The pill only renders when we're actually
 *     live, so it never lies.
 *   • Visible on mobile too — most of our traffic is mobile and the
 *     previous "Watch Live" link was desktop-only.
 *   • Uses the shared {@link useLiveStatus} hook → zero extra network
 *     cost; same poll loop the /live page already runs.
 *
 * Visual:
 *   Wraps the project's <TicketBadge> primitive with the new `on-air`
 *   variant (red, perforated stub edges, dotted inner border, pulsing
 *   dot) so it slots into the existing site aesthetic instead of
 *   inventing a one-off shape. The headline is desktop-only — phones
 *   collapse to dot + "LIVE" so the navbar doesn't crowd the centered
 *   logo.
 *
 * Returns null when offline.
 */
import React from "react";
import { Link } from "react-router-dom";
import { TicketBadge } from "../common/ticket-badge/TicketBadge";
import { useLiveStatus } from "./useLiveStatus";
import "./LiveNowPill.css";

const LiveNowPill: React.FC = () => {
  const { isLive, headline, youtubeTitle } = useLiveStatus();
  if (!isLive) return null;

  // Prefer YouTube's broadcast title (real-time, viewer-facing) but
  // fall back to the editorial headline for non-YouTube live sources
  // (manual override, status-state-driven legacy mode).
  const display = (youtubeTitle || headline || "").trim();

  return (
    <Link
      to="/live"
      className="live-now-pill-link"
      aria-label={display ? `Live now: ${display}. Click to watch.` : "Live now. Click to watch"}
      title={display}
    >
      <TicketBadge variant="on-air" showDot className="live-now-pill__badge">
        <span className="live-now-pill__label">LIVE</span>
        {display && (
          <span className="live-now-pill__headline">
            <span className="live-now-pill__sep" aria-hidden="true">·</span>
            {display}
          </span>
        )}
      </TicketBadge>
    </Link>
  );
};

export default LiveNowPill;
